import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type {
  CreateAdoCrInput,
  AdoChangeRequest,
  AdoPathNode,
  AdoPerson,
  CreateReleaseInput,
  ListReleasesParams,
  Release,
  ReleaseCalendarApi,
  Sprint,
  Team,
  UpdateReleaseInput,
} from './types';

/**
 * @name HttpReleaseCalendarClient
 * @description HTTP client that talks to the release-calendar backend plugin
 *              via Backstage's discovery and fetch APIs.
 */
export class HttpReleaseCalendarClient implements ReleaseCalendarApi {
  readonly #discovery: DiscoveryApi;
  readonly #fetchApi: FetchApi;

  constructor(discovery: DiscoveryApi, fetchApi: FetchApi) {
    this.#discovery = discovery;
    this.#fetchApi  = fetchApi;
  }

  async #request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.#discovery.getBaseUrl('release-calendar');
    const res = await this.#fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
    if (!res.ok) {
      let detail: string;
      try {
        const body = await res.json() as { error?: unknown; message?: string };
        // Backstage wraps errors as { error: { name, message } }; also handle
        // our own { error: "string" } shape and a bare { message }.
        const e = body.error;
        if (typeof e === 'string') {
          detail = e;
        } else if (e && typeof e === 'object' && 'message' in e && typeof (e as any).message === 'string') {
          detail = (e as any).message;
        } else if (typeof body.message === 'string') {
          detail = body.message;
        } else {
          detail = JSON.stringify(body);
        }
      } catch {
        detail = await res.text();
      }
      const err = new Error(detail || `Request failed (${res.status})`);
      (err as any).status = res.status;
      throw err;
    }
    if (res.status === 204 || res.headers.get('content-length') === '0') {
      return undefined as unknown as T;
    }
    return res.json() as Promise<T>;
  }

  async listReleases(params?: ListReleasesParams): Promise<Release[]> {
    const qs = new URLSearchParams();
    if (params?.teamId)      qs.set('teamId',      params.teamId);
    if (params?.environment) qs.set('environment', params.environment);
    if (params?.status)      qs.set('status',      params.status);
    if (params?.fromDate)    qs.set('fromDate',     params.fromDate);
    if (params?.toDate)      qs.set('toDate',       params.toDate);
    const query = qs.toString() ? `?${qs}` : '';
    return this.#request<Release[]>(`/releases${query}`);
  }

  async getRelease(id: string): Promise<Release> {
    return this.#request<Release>(`/releases/${id}`);
  }

  async createRelease(input: CreateReleaseInput): Promise<Release> {
    return this.#request<Release>('/releases', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async updateRelease(input: UpdateReleaseInput): Promise<Release> {
    const { id, ...body } = input;
    return this.#request<Release>(`/releases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async deleteRelease(id: string, opts?: { deleteAdo?: boolean }): Promise<void> {
    const q = opts?.deleteAdo === false ? '?deleteAdo=false' : '';
    await this.#request<void>(`/releases/${id}${q}`, { method: 'DELETE' });
  }

  async listTeams(): Promise<Team[]> {
    return this.#request<Team[]>('/teams');
  }

  async getAdoCr(crId: string): Promise<AdoChangeRequest> {
    return this.#request<AdoChangeRequest>(`/cr/${crId}`);
  }

  async createAdoCr(input: CreateAdoCrInput): Promise<AdoChangeRequest> {
    return this.#request<AdoChangeRequest>(`/cr`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async searchPeople(query: string): Promise<AdoPerson[]> {
    const q = query.trim();
    if (q.length < 2) return [];
    return this.#request<AdoPerson[]>(`/people?q=${encodeURIComponent(q)}`);
  }

  async searchAreaPaths(query: string): Promise<AdoPathNode[]> {
    return this.#request<AdoPathNode[]>(`/area-paths?q=${encodeURIComponent(query)}`);
  }

  async searchIterations(query: string): Promise<AdoPathNode[]> {
    return this.#request<AdoPathNode[]>(`/iterations?q=${encodeURIComponent(query)}`);
  }

  async listImpactedProducts(): Promise<string[]> {
    return this.#request<string[]>('/impacted-products');
  }

  async listSprints(params?: { fromDate?: string; toDate?: string }): Promise<Sprint[]> {
    const qs = new URLSearchParams();
    if (params?.fromDate) qs.set('fromDate', params.fromDate);
    if (params?.toDate)   qs.set('toDate',   params.toDate);
    const query = qs.toString() ? `?${qs}` : '';
    return this.#request<Sprint[]>(`/sprints${query}`);
  }

}
